/**
 * 불납결손 PoC
 * - 수입결의 연계 불납결손 그리드 · 저장/승인요청/승인
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import {
  FormArea,
  CollapsibleSearchPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import { showAlert, showConfirm } from 'utils/toastBridge';
import {
  STATUS,
  createWriteoffUid,
  emptyWriteoffRow,
  loadWriteoffs,
  saveWriteoffs,
  loadResolutionBundle,
  requestWriteoffApproval,
  approveWriteoff,
  statusLabel,
  todayYmd,
} from 'income/incomeStore';

const GRID_ID = 'income-writeoff';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  height: 100%;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Hint = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  padding: 0 2px;
`;

export default function IncomeUncollectible() {
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: { fiscalYear: getCurrentFiscalYear() },
  });
  const { getValues, reset } = methods;

  const gridRef = useRef(null);
  const [resolutionOptions, setResolutionOptions] = useState([]);

  const resolutionComboOptions = useMemo(
    () => [{ value: '', label: '(선택)' }, ...resolutionOptions.map((o) => ({ value: o.value, label: o.label }))],
    [resolutionOptions]
  );

  const columns = useMemo(
    () => [
      hiCol.edComboColumn({
        header: '수입결의',
        field: 'incomeResolutionUid',
        size: 200,
        options: resolutionComboOptions,
        editable: true,
      }),
      hiCol.edNumberColumn({ header: '결손금액', field: 'writeoffAmt', size: 110, align: 'right', editable: true }),
      hiCol.edDateColumn({ header: '결손일', field: 'writeoffDate', size: 110, align: 'center', editable: true }),
      hiCol.edTextColumn({ header: '법적근거', field: 'legalBasis', size: 140, editable: true }),
      hiCol.edTextColumn({ header: '사유', field: 'reason', size: 160, editable: true }),
      hiCol.roTextColumn({ header: '상태', field: 'statusNm', size: 80, align: 'center' }),
      hiCol.roTextColumn({ header: '전표', field: 'slipYn', size: 60, align: 'center' }),
    ],
    [resolutionComboOptions, resolutionOptions]
  );

  const grid = useHiGridHook({
    gridRef,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: emptyWriteoffRow({ writeoffDate: todayYmd(), mod: 'A' }),
  });

  const dims = useCallback(
    () => ({
      tenantCode,
      fiscalYear: String(getValues('fiscalYear') || '').trim(),
    }),
    [getValues, tenantCode]
  );

  const loadResolutionOptions = useCallback(async () => {
    const d = dims();
    const bundle = await loadResolutionBundle(d);
    const opts = (bundle.masters || [])
      .filter((m) => m.mod !== 'D' && m.status === STATUS.APPROVED)
      .map((m) => ({
        value: m.resolutionUid,
        label: `${m.resolutionNo} · ${m.deptName || ''}`,
        resolutionNo: m.resolutionNo,
      }));
    setResolutionOptions(opts);
    return opts;
  }, [dims]);

  const handleSearch = useCallback(async () => {
    const d = dims();
    if (!d.fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    await loadResolutionOptions();
    const rows = (await loadWriteoffs(d))
      .filter((r) => r.mod !== 'D')
      .map((r) => ({
        ...r,
        statusNm: statusLabel(r.status),
      }));
    gridRef.current?.setData?.(rows);
  }, [dims, loadResolutionOptions]);

  useEffect(() => {
    reset({ fiscalYear: getCurrentFiscalYear() });
  }, [reset]);

  const handleAdd = useCallback(async () => {
    const row = emptyWriteoffRow({
      writeoffUid: createWriteoffUid(),
      writeoffDate: todayYmd(),
      status: STATUS.DRAFT,
      mod: 'A',
    });
    gridRef.current?.setData?.([...(gridRef.current?.getData?.() || []), row]);
  }, []);

  const handleSave = useCallback(async () => {
    const d = dims();
    if (!d.fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    const gridRows = gridRef.current?.getData?.() || [];
    const all = await loadWriteoffs(d);
    const byUid = new Map(all.map((r) => [r.writeoffUid, r]));

    gridRows.forEach((row) => {
      const uid = row.writeoffUid || createWriteoffUid();
      const prev = byUid.get(uid);
      const res = resolutionOptions.find((o) => o.value === row.incomeResolutionUid);
      byUid.set(uid, {
        ...emptyWriteoffRow(),
        ...(prev || {}),
        ...row,
        writeoffUid: uid,
        incomeResolutionNo: res?.resolutionNo || row.incomeResolutionNo || prev?.incomeResolutionNo || '',
        mod: prev ? (row.mod === 'D' ? 'D' : 'U') : row.mod || 'A',
      });
    });

    await saveWriteoffs(d, Array.from(byUid.values()));
    await handleSearch();
    showAlert('저장했습니다.', 'success');
  }, [dims, resolutionOptions, handleSearch]);

  const handleRequest = useCallback(async () => {
    const sel = gridRef.current?.getSelectedRow?.();
    if (!sel?.writeoffUid) {
      showAlert('승인요청할 불납결손 건을 선택하세요.');
      return;
    }
    if (sel.status !== STATUS.DRAFT && sel.status !== STATUS.REJECTED) {
      showAlert('작성중·반려 상태만 승인요청할 수 있습니다.');
      return;
    }
    const ok = await showConfirm('승인요청할까요?');
    if (!ok) return;
    await handleSave();
    const d = dims();
    const all = await loadWriteoffs(d);
    const next = requestWriteoffApproval(all, sel.writeoffUid);
    await saveWriteoffs(d, next);
    await handleSearch();
    showAlert('승인요청했습니다.', 'success');
  }, [handleSave, dims, handleSearch]);

  const handleApprove = useCallback(async () => {
    const sel = gridRef.current?.getSelectedRow?.();
    if (!sel?.writeoffUid) {
      showAlert('승인할 불납결손 건을 선택하세요.');
      return;
    }
    if (sel.status !== STATUS.REQUESTED) {
      showAlert('승인요청 상태만 승인할 수 있습니다.');
      return;
    }
    const ok = await showConfirm('승인할까요? (전표 stub)');
    if (!ok) return;
    const d = dims();
    const all = await loadWriteoffs(d);
    const next = approveWriteoff(all, sel.writeoffUid);
    await saveWriteoffs(d, next);
    await handleSearch();
    showAlert('승인했습니다.', 'success');
  }, [dims, handleSearch]);

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'fiscalYear',
          label: '회계연도',
          type: 'fiscalYear',
          width: '88px',
          labelWidth: SEARCH_LABEL_W,
        },
      ],
    ],
    []
  );

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      { id: 'add', label: '추가', icon: '➕', onClick: handleAdd, hotkey: 'F8' },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: () => grid.handleDelete?.(),
        hotkey: 'F9',
        variant: 'danger',
      },
      { id: 'save', label: '저장', icon: '✅', onClick: handleSave, hotkey: 'F10', variant: 'success' },
    ],
    [handleSearch, handleAdd, handleSave, grid]
  );

  const extraButtons = useMemo(
    () => [
      { id: 'request', label: '승인요청', onClick: handleRequest },
      { id: 'approve', label: '승인', onClick: handleApprove, variant: 'primary' },
    ],
    [handleRequest, handleApprove]
  );

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <Stack>
          <CollapsibleSearchPanel storageKey="income-writeoff" prefsUserId={prefsUserId} title="조회조건">
            <FormArea compact fields={searchFields} />
          </CollapsibleSearchPanel>

          <Hint>승인된 수입결의 연계 불납결손 · 승인 시 slipYn=Y 전표 stub</Hint>

          <Panel>
            <GridBody>
              <HiGridComponent
                ref={gridRef}
                columns={columns}
                showRowNumber
                rowNumberSize={48}
                enableColumnFilters
                enableContextMenu
                enableRowEditMenu
                enableCut
                enableFindReplace
                allowReplace
                enableHeaderMenu
                enableSelectionStats
                enableQuickExport
                exportFileName="불납결손"
                gridId={GRID_ID}
                prefsUserId={prefsUserId}
                {...(grid.contextMenuProps || {})}
              />
            </GridBody>
          </Panel>
        </Stack>
      </FormProvider>
    </PageContainer>
  );
}
